import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import AuthClient from '../data/api/AuthClient';
import '../styles/AuthStyles.css';
import '../styles/LoginStyle.css';

const ForgotPasswordComponent: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await AuthClient.forgotPassword({
        email_address: email.trim().toLowerCase(),
        entity_type: 'INTERVIEWIQ_ADMIN',
      });
      if (res.detail) {
        setSent(true);
      } else {
        setError(res.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-form-wrapper">
      <div className="brand-header" style={{ justifyContent: 'center' }}>
        <img src="/logo_login_name.png" alt="InterviewIQ Logo" className="brand-logo-img" />
      </div>

      {/* <div className="portal-label">CORPORATE PORTAL</div> */}

      {!sent ? (
        <>
          <h2 className="welcome-title">Forgot Password?</h2>
          <p className="welcome-subtitle">Enter your business email and we'll send you a secure link to reset your password.</p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group border-input-group">
              <label className="field-label">Email Address</label>
              <input
                id="forgot-email"
                className="form-input-line"
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                autoFocus
              />
            </div>

            {error && (
              <motion.div className="auth-error-banner" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}>
                <AlertCircle size={15} /> {error}
              </motion.div>
            )}

            <button id="send-reset-link" type="submit" className="btn-primary" disabled={loading}>
              {loading ? <span className="auth-spinner" /> : <><Send size={16} /> Send Reset Link</>}
            </button>
          </form>

          <div className="register-prompt" style={{ marginTop: 24 }}>
            <span className="register-link" onClick={() => navigate('/login')} style={{ cursor: 'pointer' }}>
              <ArrowLeft size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Back to Login
            </span>
          </div>
        </>
      ) : (
        <div className="auth-success-state">
          <motion.div
            className="success-icon-ring"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          >
            <CheckCircle2 size={44} color="#0d6e5b" />
          </motion.div>
          <h2 className="welcome-title" style={{ textAlign: 'center' }}>Check Your Email</h2>
          <p className="welcome-subtitle" style={{ textAlign: 'center' }}>
            We've sent a password reset link to <strong>{email}</strong>. Please check your inbox and spam folder.
          </p>
          <button className="btn-primary" onClick={() => navigate('/login')} style={{ marginTop: 20 }}>
            Back to Login
          </button>
        </div>
      )}
    </div>
  );
};

export default ForgotPasswordComponent;
